import { useCallback, useState } from 'react';
import { Check, Copy } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/shared/lib/cn';
import { Button } from '@/shared/ui/button';

export interface TooltipCopyLine {
  label?: string;
  value: string;
}

interface TooltipCopyContentProps {
  lines: TooltipCopyLine[];
  className?: string;
}

async function copyText(text: string) {
  await navigator.clipboard.writeText(text);
}

/** Conteúdo de tooltip com botões de copiar por linha e "Copiar tudo". */
export function TooltipCopyContent({ lines, className }: TooltipCopyContentProps) {
  const items = lines.filter((l) => l.value.trim());
  const [copiedKey, setCopiedKey] = useState<string | null>(null);

  const handleCopy = useCallback(async (text: string, key: string, toastMsg = 'Copiado!') => {
    try {
      await copyText(text);
      setCopiedKey(key);
      toast.success(toastMsg, { duration: 1800 });
      setTimeout(() => setCopiedKey((k) => (k === key ? null : k)), 1600);
    } catch {
      toast.error('Não foi possível copiar.');
    }
  }, []);

  if (items.length === 0) {
    return <span className="text-sm text-muted-foreground">—</span>;
  }

  const allText = items.map((l) => (l.label ? `${l.label}: ${l.value}` : l.value)).join('\n');

  return (
    <div
      className={cn('space-y-2', className)}
      onPointerDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    >
      {items.map((line, i) => {
        const key = `line-${i}-${line.value}`;
        const copied = copiedKey === key;
        const display = line.label ? `${line.label}: ${line.value}` : line.value;
        return (
          <div key={key} className="flex items-start gap-1.5">
            <div className="min-w-0 flex-1">
              {line.label ? (
                <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                  {line.label}
                </p>
              ) : null}
              <p className="text-sm leading-snug break-words">{line.value}</p>
            </div>
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              className="size-7 shrink-0 text-muted-foreground hover:text-foreground"
              aria-label={`Copiar ${display}`}
              onClick={() => void handleCopy(line.value, key)}
            >
              {copied ? <Check className="size-3.5 text-success" /> : <Copy className="size-3.5" />}
            </Button>
          </div>
        );
      })}
      {items.length > 1 ? (
        <Button
          type="button"
          variant="secondary"
          size="sm"
          className="mt-1 h-8 w-full gap-1.5 text-xs"
          onClick={() => void handleCopy(allText, 'all', 'Tudo copiado!')}
        >
          {copiedKey === 'all' ? <Check className="size-3.5" /> : <Copy className="size-3.5" />}
          Copiar tudo
        </Button>
      ) : null}
    </div>
  );
}
